import { useEffect, useMemo, useState } from "react";

import { LegendWithOneLineCheckboxFlexWithSubtitle } from "@similarweb/ui-components";

import { Legends } from "app/components/React/Legends/Legends";
import type { ChannelKeyName, MetricKeyNames, SegmentId } from "app/constants/MmxChannels";
import { ChannelsObj } from "app/pages/segments/mmx/ChannelsObj";
import { EngagementVerticals } from "app/pages/segments/mmx/EngagementVerticals";
import type { ICustomSegment, ICustomSegmentGroupWebsite } from "app/services/segments/segmentsApiService";
import { SEGMENT_TYPES } from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { allTrackers } from "app/services/track/track";

import { SegmentLegendWrapper } from "./styledComponents";

interface ISegmentDomainLegendsProps {
    selectedDisplayTypeIndex: number;
    selectedMetric: MetricKeyNames;
    selectedRows: any;
    allSegments: any;
    currentChannel: ChannelKeyName;
    setHiddenLegends?: any;
    hiddenLegends?: string[];
    groups?: any[];
    params?: any;
    data?: any;
}

interface IRow {
    id: SegmentId;
    selectionColor: string;
}

interface ILegend {
    data: string;
    segmentId: SegmentId;
    gridRowGap: string;
    name: string;
    subtitleText: string;
    hidden: boolean;
    color: string;
    isWinner: boolean;
}

export const SegmentDomainLegends = (props: ISegmentDomainLegendsProps) => {
    const {
        selectedDisplayTypeIndex,
        selectedMetric,
        selectedRows,
        allSegments,
        currentChannel,
        setHiddenLegends,
        hiddenLegends,
        groups,
        params,
        data,
    } = props;

    const getLegendItems = useMemo(() => {
        const legends: ILegend[] = selectedRows?.map((row: IRow) => {
            if (
                row.id &&
                !(
                    SegmentsUtils.getSegmentIdTypeByKey(row.id)[1] === SEGMENT_TYPES.WEBSITE &&
                    currentChannel === ChannelsObj.InternalReferrals
                )
            ) {
                const [memberObj, memberType] = SegmentsUtils.getSegmentObjectByKey(row.id, {
                    segments: allSegments,
                    websites: groups?.find((group) => group.id === params.id)?.websites,
                });
                const filter =
                    selectedDisplayTypeIndex === 1 && selectedMetric === EngagementVerticals.Visits.name
                        ? EngagementVerticals[selectedMetric].percentFilter
                        : EngagementVerticals[selectedMetric].filter;
                const dataKey =
                    selectedDisplayTypeIndex === 1 && selectedMetric === EngagementVerticals.Visits.name
                        ? "Percentage"
                        : "Value";
                switch (memberType) {
                    case SEGMENT_TYPES.SEGMENT: {
                        const segmentObj = memberObj as ICustomSegment;
                        const segmentValue =
                            data[row.id]?.Monthly?.[selectedMetric]?.[currentChannel]?.Total?.[dataKey];
                        return {
                            data:
                                segmentValue === 0 || segmentValue === undefined
                                    ? "-"
                                    : filter[0]()(segmentValue, filter[1]),
                            segmentId: row.id,
                            gridRowGap: "0px",
                            name: segmentObj.domain,
                            subtitleText: segmentObj.segmentName,
                            hidden: hiddenLegends?.includes(row.id),
                            color: row.selectionColor,
                            isWinner: false,
                        } as ILegend;
                    }
                    case SEGMENT_TYPES.WEBSITE: {
                        const websiteObj = memberObj as ICustomSegmentGroupWebsite;
                        const websiteValue =
                            data[row.id]?.Monthly?.[selectedMetric]?.[currentChannel]?.Total?.[dataKey];
                        return {
                            data:
                                websiteValue === 0 || websiteValue === undefined
                                    ? "-"
                                    : filter[0]()(websiteValue, filter[1]),
                            segmentId: row.id,
                            gridRowGap: "0px",
                            name: websiteObj.domain,
                            subtitleText: websiteObj.domain,
                            hidden: hiddenLegends?.includes(row.id),
                            color: row.selectionColor,
                            isWinner: false,
                        } as ILegend;
                    }
                }
            }
        });
        return legends.filter((legend) => legend !== undefined);
    }, [selectedRows, selectedDisplayTypeIndex, data, currentChannel, selectedMetric, allSegments, groups, params?.id]);

    const [legendItems, setLegendItems] = useState(() => {
        return getLegendItems;
    });
    useEffect(() => {
        setLegendItems(getLegendItems);
    }, [selectedMetric, selectedRows, selectedDisplayTypeIndex, currentChannel, data, allSegments, groups, params?.id]);

    const onLegendClick = (filter: ILegend) => {
        const action = filter.hidden ? "add" : "remove";
        const filteredChannels: any[] = [];
        const filterChannels = legendItems?.map((f) => {
            if (f.segmentId === filter.segmentId) {
                f.hidden = !f.hidden;
            }
            if (f.hidden) {
                filteredChannels.push(f.segmentId);
            }
            return f;
        });
        setLegendItems(filterChannels);
        setHiddenLegends(filteredChannels);
        allTrackers.dispatchTrackEvent(
            "segments_analysis.marketing_channels.group.checkbox_filters",
            "click",
            "segments_analysis.marketing_channels.group.checkbox_filters",
        );
    };
    return (
        <Legends
            legendComponent={LegendWithOneLineCheckboxFlexWithSubtitle}
            legendComponentWrapper={SegmentLegendWrapper}
            legendItems={legendItems}
            toggleSeries={onLegendClick}
            gridDirection="column"
            textMaxWidth={window.innerWidth < 1680 ? (window.innerWidth > 1366 ? "125px" : "100px") : "150px"}
        />
    );
};
